import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #f5f5f5 0%, #fff7ed 100%);
  padding: 3rem;
  position: relative;
  overflow: hidden;
`;

const Title = styled(motion.h1)`
  font-size: 3rem;
  font-weight: 700;
  color: #042E4B;
  margin-bottom: 1rem;
  text-align: center;
  max-width: 1000px;
`;

const Subtitle = styled(motion.p)`
  font-size: 1.2rem;
  color: #607C90;
  margin-bottom: 3rem;
  text-align: center;
  max-width: 900px;
  line-height: 1.6;
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  max-width: 1400px;
  width: 100%;
  align-items: center;
`;

const ScreenshotsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  align-items: center;
`;

const ScreenshotFrame = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 0.8rem;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
  width: 100%;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 25px;
    background: linear-gradient(90deg, #042E4B, #0A4A6F);
    border-radius: 16px 16px 0 0;
  }
  
  &::after {
    content: '● ● ●';
    position: absolute;
    top: 0;
    left: 0.8rem;
    height: 25px;
    display: flex;
    align-items: center;
    font-size: 0.6rem;
    color: rgba(255, 255, 255, 0.7);
    letter-spacing: 0.2rem;
  }
`;

const ScreenshotImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 8px;
  margin-top: 25px;
`;

const BenefitsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const BenefitCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 35px rgba(0, 0, 0, 0.15);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #EF443C, #C43931);
  }
`;

const BenefitIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #EF443C, #C43931);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  box-shadow: 0 4px 12px rgba(239, 68, 60, 0.3);
  flex-shrink: 0;
`;

const BenefitContent = styled.div`
  flex: 1;
`;

const BenefitName = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  color: #042E4B;
  margin-bottom: 0.5rem;
`;

const BenefitDescription = styled.div`
  font-size: 1rem;
  color: #607C90;
  line-height: 1.4;
`;

const IntegrationBadge = styled(motion.div)`
  background: linear-gradient(135deg, #042E4B, #0A4A6F);
  color: white;
  padding: 1rem 2rem;
  border-radius: 25px;
  font-size: 1.2rem;
  font-weight: 600;
  margin-top: 2rem;
  box-shadow: 0 6px 20px rgba(4, 46, 75, 0.3);
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
`;

const Slide12_5IntegracaoScreenshot: React.FC = () => {
  const benefits = [
    {
      icon: '🔗',
      name: 'Conectividade Total',
      description: 'Todos os módulos trabalham em perfeita harmonia para máxima eficiência',
    },
    {
      icon: '⚡',
      name: 'Fluxo Otimizado',
      description: 'Dados fluem automaticamente entre as diferentes áreas da plataforma',
    },
    {
      icon: '🎯',
      name: 'Visão Unificada',
      description: 'Uma única fonte da verdade para todas as informações do seu negócio',
    },
  ];

  return (
    <Container>
      <Title
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        Ecossistema Integrado e Poderoso
      </Title>

      <Subtitle
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        Veja como todos os módulos da ThinkWay se conectam para formar uma solução completa
      </Subtitle>

      <MainContent>
        <ScreenshotsGrid>
          <ScreenshotFrame
            initial={{ x: -30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <ScreenshotImage
              src="/screenshots/screenshot9.png"
              alt="Módulo de Gestão"
            />
          </ScreenshotFrame>

          <ScreenshotFrame
            initial={{ x: 30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <ScreenshotImage
              src="/screenshots/screenshot10.png"
              alt="Interface Avançada"
            />
          </ScreenshotFrame>

          <ScreenshotFrame
            initial={{ x: -30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <ScreenshotImage
              src="/screenshots/screenshot11.png"
              alt="Dashboard Executivo"
            />
          </ScreenshotFrame>

          <ScreenshotFrame
            initial={{ x: 30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <ScreenshotImage
              src="/screenshots/screenshot5.png"
              alt="Relatórios Avançados"
            />
          </ScreenshotFrame>
        </ScreenshotsGrid>

        <BenefitsSection>
          {benefits.map((benefit, index) => (
            <BenefitCard
              key={benefit.name}
              initial={{ x: 50, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{
                duration: 0.4,
                delay: 0.4 + index * 0.1,
                type: 'spring',
                stiffness: 100,
              }}
            >
              <BenefitIcon>{benefit.icon}</BenefitIcon>
              <BenefitContent>
                <BenefitName>{benefit.name}</BenefitName>
                <BenefitDescription>{benefit.description}</BenefitDescription>
              </BenefitContent>
            </BenefitCard>
          ))}

          <IntegrationBadge
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.8 }}
          >
            <span>🌐</span>
            <span>Plataforma Completa</span>
          </IntegrationBadge>
        </BenefitsSection>
      </MainContent>
    </Container>
  );
};

export default Slide12_5IntegracaoScreenshot;